import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const ApplicationMain = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState(null);

  useEffect(() => {
    const id = searchParams.get('customerid');
    if (id) {
      setCustomerId(id);
    } else {
      // Redirect to home if no customer ID
      navigate('/');
    }
  }, [searchParams, navigate]);

  if (!customerId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 md:p-12">
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
        </div>

        {/* Success Message */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Thank You for Your Submission!
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Your application has been received successfully.
          </p>
          <div className="inline-block bg-blue-50 px-4 py-2 rounded-lg mt-4">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Customer ID:</span>{' '}
              <span className="text-blue-600 font-mono">{customerId}</span>
            </p>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">What Happens Next?</h2>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                1
              </span>
              <p className="text-gray-700">
                Our team will review your information within <strong>24 hours</strong>
              </p>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                2
              </span>
              <p className="text-gray-700">
                You'll receive a confirmation email with next steps
              </p>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                3
              </span>
              <p className="text-gray-700">
                Our trademark specialists will contact you to begin the process
              </p>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() => navigate('/')}
            className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white"
          >
            Return to Home
          </Button>
          <Button
            onClick={() => navigate('/about-us')}
            variant="outline"
            className="flex-1"
          >
            Learn More About Us
          </Button>
        </div>

        {/* Support Info */}
        <div className="mt-8 pt-8 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-600 mb-2">
            Need immediate assistance?
          </p>
          <p className="text-lg font-semibold text-gray-900">
            Call us at: <a href="tel:+18554423502" className="text-blue-600 hover:underline">+1 (855) 442-3502</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ApplicationMain;
